/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ColDef, ColumnState} from 'ag-grid-community';
import isNil from 'lodash.isnil';
import {getSearchtFilter} from '@ion/applications/src/globalsearch/GlobalSearchUtils';
import {useGridRefreshAction} from '@ion/applications/src/hooks/useGridRefreshAction';
import {useGroupRefresh} from '@ion/applications/src/hooks/useGroupRefresh';
import {translate} from '@ion/core/src/locale/locale';
import ApplicationFeatures from '@ion/core/src/security/ApplicationFeatures';
import {
  AccountLink,
  CRUDPermissions,
  GenericObject,
  Group
} from '@ion/core/src/types/entities';
import {booleanToString} from '@ion/core/src/utils/BooleanUtils';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import {getUniqItems} from '@ion/dls/src/editors/ListEditorUtils';
import {
  defaultDateFilterParams,
  defaultTextFilterParams
} from '@ion/dls/src/grid/ColumnDefinitions';
import {definitionsToState} from '@ion/dls/src/grid/ColumnResolver';
import {
  IonGrid,
  IonGridAPI,
  ViewExcludeInheritedGroupProps
} from '@ion/dls/src/grid/IonGrid';
import {callbackInterceptor, SortModelType} from '@ion/dls/src/grid/IonGridUtils';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {PAGE_SIZES} from '@ion/dls/src/grid/settings';
import {useColumnChooser} from '@ion/dls/src/grid/useColumnChooser';
import {useColumnState} from '@ion/dls/src/grid/useColumnState';
import {useBulkOperationResults} from '@ion/dls/src/hooks/useBulkOperationResults';
import {useGridDataUpdate} from '@ion/dls/src/hooks/useGridDataUpdate';
import {GridPageSizeProps} from '@ion/dls/src/types/types';
import {determineColumnFilter, GridFilterType} from '@ion/dls/src/utils/GridUtils';
import {createModularSearchFilters} from '@ion/features/src/search/utils';
import {exportCTCAdminGroups} from '../../../store/actions/exporter';
import {dispatchGridColumnsChange} from '../../../store/helpers';
import {gridDefaultPageSize} from '../../../utils/helpers';
import {AccountCell} from '../accounts/AccountCell';
import {GroupRowEditor} from './GroupRowEditor';
import {getMemberCount} from './GroupsGridUtils';
import {GroupsItemCountCell} from './GroupsItemCountCell';
import {
  deleteAdminGroups,
  getGroups,
  groupsGridRefreshed,
  NAME,
  SET_GROUPS_GRID_COLUMNS
} from './state';
import {
  groupsAddResultSelector,
  groupsDestroyResultSelector,
  groupsEditResultSelector,
  groupsGridColumnStateSelector,
  groupsModularSearchSelector
} from './state-selectors';

export enum GroupsColumns {
  NAME = 'name',
  ITEM_COUNT = 'membersCountUI',
  LAST_MODIFIED = 'lastModifiedOnUI',
  USERNAME = 'username',
  NOTES = 'notes',
  CREATED = 'createdOnUI',
  STATUS = 'status',
  DELETED = 'deletedOn',
  ACCOUNT = 'accountUI',
  INHERITABLE = 'inheritableUI'
};

const DEFAULT_FILTERED_COLUMNS = [
  GroupsColumns.NAME,
  GroupsColumns.LAST_MODIFIED
];

export interface StatusProps {
  showStatus?: boolean;
};

export interface GroupsGridProps extends
  StatusProps,
  GridPageSizeProps,
  ViewExcludeInheritedGroupProps {
  onSelectAccount?: (value: AccountLink) => void;
  includeGlobalSearch?: boolean;
  isCtcAdmin?: boolean;
  searchKeyword?: string;
  enableSelectionsExport?: boolean;
  filteredColumns?: GroupsColumns[];
  refreshInitialization?: Function;
  viewSubGroups?: boolean;
  groupsPermissions?: CRUDPermissions;
  hasInheritableGroups?: boolean;
  Features?: ApplicationFeatures;
  ancestorsGroups?: GenericObject;
}

/**
 * Grid to be rendered on the schedule config Groups page
 */
export const GroupsGrid = ({
  onSelectAccount,
  isCtcAdmin,
  Features,
  setGridPageSize,
  refreshInitialization,
  viewSubGroups,
  groupsPermissions,
  ancestorsGroups,
  setTotalRowsCount,
  searchKeyword = '',
  gridPageSize = gridDefaultPageSize,
  filteredColumns = DEFAULT_FILTERED_COLUMNS,
  includeGlobalSearch = false,
  enableSelectionsExport = false,
  viewExcludeInheritedGroup = false,
  hasInheritableGroups = false,
  showStatus = false,
  usePreferedPageSize = false
}: GroupsGridProps, ref) => {
  const dispatch                   = useDispatch();
  const modularSearchParams        = useSelector(groupsModularSearchSelector);
  const addResult                  = useSelector(groupsAddResultSelector);
  const editResult                 = useSelector(groupsEditResultSelector);
  const deleteResult               = useSelector(groupsDestroyResultSelector);
  const {bulkDeleteResult}         = useBulkOperationResults('groups');
  const columnState: ColumnState[] = useSelector(groupsGridColumnStateSelector);

  const [refreshRequired, setRefreshRequired] = useState(false);
  const refreshGrid                           = useGroupRefresh();
  const ionGridRef                            = useRef<IonGridAPI>();
  const defaultColumnDefinitions              = useMemo(() =>
    getColumnDefinitions({includeGlobalSearch, filteredColumns, showStatus, hasInheritableGroups}),
  [includeGlobalSearch, filteredColumns, showStatus]);

  const {definitions, defaultDefinitions}     = useColumnState({
    columnDefinitions : defaultColumnDefinitions,
    columnState
  });

  const {
    showColumnChooser,
    columnDefinitions,
    onColumnChooserSave,
    toggleColumnChooser
  } = useColumnChooser(definitions);

  useGridRefreshAction(refreshRequired, groupsGridRefreshed);

  useGridDataUpdate(NAME, ionGridRef.current, groupsGridRefreshed);

  /**
   * Returns a reference to the grids public api ..
   */
  useImperativeHandle(ref, () => ({
    toggleColumnChooser,
    onExport,
    onRefresh
  }));

  useEffect(() => {
    const refresh = Boolean(
      addResult ||
      editResult ||
      deleteResult
    );
    setRefreshRequired(refresh);
  }, [addResult, deleteResult, editResult]);

  useEffect(() => {
    if (includeGlobalSearch && !isNil(searchKeyword)) {
      setRefreshRequired(!refreshRequired);
    }
  }, [includeGlobalSearch, searchKeyword]);

  const onColumnSelectionsUpdate = function(columns: ColDef[]): void {
    onColumnChooserSave(columns);
    dispatch(dispatchGridColumnsChange(definitionsToState(columns), NAME, SET_GROUPS_GRID_COLUMNS) as any);
  };

  const onColumnStateUpdate = function(state: ColumnState[]): void {
    if (!includeGlobalSearch) {
      dispatch(dispatchGridColumnsChange(state, NAME, SET_GROUPS_GRID_COLUMNS) as any);
    }
  };

  const onExport = function() {
    const params = ionGridRef.current?.getExportParams(
      viewSubGroups,
      enableSelectionsExport,
      createModularSearchFilters(modularSearchParams)
    );
    params.excludeInheritedGroups = viewExcludeInheritedGroup;
    dispatch(exportCTCAdminGroups(params) as any);
  };

  const onRefresh = function() {
    setRefreshRequired(true);
  };

  function onDeleteGroups(groups: Group[]) {
    dispatch(deleteAdminGroups(groups));
  };

  const attributeMappings = {
    name             : 'name',
    membersCountUI   : 'membersCount',
    username         : 'username',
    lastModifiedOnUI : 'lastModifiedOn',
    createdOnUI      : 'createdOn',
    notes            : 'notes',
    accountUI        : 'account',
    inheritableUI    : 'inheritable'
  };

  const defaultSort: SortModelType = {
    colId : 'name',
    sort  : 'asc'
  };

  const getFilter = (filter) => {
    if (includeGlobalSearch) {
      return getSearchtFilter('name', searchKeyword, filter);
    }
    return filter;
  };

  function getGroupsData({
    page,
    pageSize,
    sort,
    filter,
    callback
  }) {
    dispatch(getGroups({
      filter                 : getFilter(filter),
      callback               : callbackInterceptor(callback, setTotalRowsCount),
      includeSubGroups       : viewSubGroups as boolean,
      includeGlobalSearch    : includeGlobalSearch,
      excludeInheritedGroups : viewExcludeInheritedGroup,
      pageId                 : page + 1,
      pageSize,
      sort
    }) as any);
  };

  return (
    <IonGrid ref={ionGridRef}
      paginationEnabled
      showCustomPaginationBar
      pageSize={gridPageSize}
      setGridPageSize={setGridPageSize}
      refreshRequired={refreshRequired || (refreshGrid && !includeGlobalSearch)}
      setRefreshRequired={setRefreshRequired}
      loadItems={getGroupsData}
      checkboxesEnabled={true}
      onBulkDelete={onDeleteGroups}
      viewExcludeInheritedGroup={viewExcludeInheritedGroup}
      augmentItems={constructGroupsGridRows}
      attributeMappings={attributeMappings}
      columnDefinitions={columnDefinitions}
      defaultDefinitions={defaultDefinitions}
      showColumnChooser={showColumnChooser}
      onColumnChooserSave={onColumnSelectionsUpdate}
      onColumnsUpdated={onColumnStateUpdate}
      onColumnChooserClose={function() {
        toggleColumnChooser(false);
      }}
      columnComponents={{
        groupMembersCount : GroupsItemCountCell,
        rowMenu           : item => (
          <GroupRowEditor group={item?.data}
            groupsPermissions={groupsPermissions}
            ancestorsGroups={ancestorsGroups}
            hasInheritableGroups={hasInheritableGroups}
            includeGlobalSearch={includeGlobalSearch} />
        ),
        accountName : (item: GridResult) => (
          <AccountCell item={item}
            isCtcAdmin={isCtcAdmin}
            Features={Features}
            refreshInitialization={refreshInitialization}
            onSelectAccount={onSelectAccount} />
        )
      }}
      viewSubGroups={viewSubGroups as boolean}
      pageSizes={PAGE_SIZES}
      defaultSort={defaultSort}
      usePreferedPageSize={usePreferedPageSize} />
  );
};

/**
 * Takes a list of schedule config Groups and constructs a list that
 * can be easily consumed by the IonGrid API
 */
export const constructGroupsGridRows = (groups: Group[]): GridResult[] => {
  return groups.map((group) => {
    const {
      name,
      membersCount,
      username,
      lastModifiedOn,
      createdOn,
      notes,
      account,
      inheritable
    } = group || {};
    return {
      ...group,
      name             : name,
      membersCountUI   : getMemberCount(membersCount),
      username         : username,
      lastModifiedOnUI : formatDateTime(lastModifiedOn),
      createdOnUI      : formatDateTime(createdOn),
      notes            : notes,
      accountUI        : account?.title,
      inheritableUI    : booleanToString(inheritable)
    };
  })as GridResult[];
};

const defaultColumn = (headerName: string, field: string, cellRender: string): ColDef => ({
  headerName,
  field,
  sortable     : false,
  filter       : false,
  resizable    : true,
  unSortIcon   : false,
  cellRenderer : cellRender,
  hide         : false,
  filterParams : defaultTextFilterParams
});

const getGroupColumnDefinition = () => ({
  field      : GroupsColumns.INHERITABLE,
  headerName : translate('groups:grid.columns.inheritable'),
  resizable  : true,
  sortable   : false,
  unSortIcon : false,
  hide       : false
});

/**
 * Returns column definitions for desktop schedule config Groups grid
 */
const getColumnDefinitionsDesktop = (
  filteredColumns: GroupsColumns[],
  showStatus: boolean,
  hasInheritableGroups: boolean
): ColDef[] => {
  const columns = [{
    headerName   : translate('groups:grid.columns.groupName'),
    field        : GroupsColumns.NAME,
    sortable     : true,
    filter       : determineColumnFilter(GroupsColumns.NAME, filteredColumns),
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultTextFilterParams
  },
  defaultColumn(translate('groups:grid.columns.itemCount'), GroupsColumns.ITEM_COUNT, 'groupMembersCount'),
  {
    headerName   : translate('groups:grid.columns.updatedOn'),
    field        : GroupsColumns.LAST_MODIFIED,
    sortable     : true,
    filter       : determineColumnFilter(GroupsColumns.LAST_MODIFIED, filteredColumns, GridFilterType.DATE),
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultDateFilterParams
  },
  defaultColumn(translate('groups:grid.columns.updatedBy'), GroupsColumns.USERNAME, null),
  defaultColumn(translate('groups:grid.columns.notes'), GroupsColumns.NOTES, null),
  defaultColumn(translate('groups:grid.columns.createdOn'), GroupsColumns.CREATED, null),
  {
    headerName         : translate('groups:grid.columns.account'),
    field              : 'accountUI',
    cellRenderer       : 'accountName',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    resizable          : true,
    unSortIcon         : true,
    hide               : false
  }];
  !showStatus && {
    headerName   : translate('groups:grid.columns.status'),
    field        : GroupsColumns.STATUS,
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultTextFilterParams
  };
  if (hasInheritableGroups) {
    columns.push(
      getGroupColumnDefinition()
    );
  }
  return columns;
};

/**
 * Returns column definitions for mobile schedule config Groups grid
 */
const getColumnDefinitionsMobile = (
  filteredColumns: GroupsColumns[],
  showStatus: boolean,
  hasInheritableGroups: boolean
): ColDef[] => {
  const columns = [{
    headerName   : translate('groups:grid.columns.groupName'),
    field        : GroupsColumns.NAME,
    sortable     : true,
    filter       : determineColumnFilter(GroupsColumns.NAME, filteredColumns),
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultTextFilterParams
  },
  defaultColumn(translate('groups:grid.columns.itemCount'), GroupsColumns.ITEM_COUNT, 'groupMembersCount'),
  {
    headerName   : translate('groups:grid.columns.updatedOn'),
    field        : GroupsColumns.LAST_MODIFIED,
    sortable     : true,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultDateFilterParams
  }, {
    headerName   : translate('groups:grid.columns.deletedOn'),
    field        : GroupsColumns.DELETED,
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  defaultColumn(translate('groups:grid.columns.updatedBy'), GroupsColumns.USERNAME, null),
  defaultColumn(translate('groups:grid.columns.notes'), GroupsColumns.NOTES, null),
  defaultColumn(translate('groups:grid.columns.createdOn'), GroupsColumns.CREATED, null),
  {
    headerName         : translate('groups:grid.columns.account'),
    field              : 'accountUI',
    cellRenderer       : 'accountName',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    resizable          : true,
    unSortIcon         : true,
    hide               : false
  }];
  !showStatus && {
    headerName   : translate('groups:grid.columns.status'),
    field        : GroupsColumns.STATUS,
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultTextFilterParams
  };
  if (hasInheritableGroups) {
    columns.push(
      getGroupColumnDefinition()
    );
  }
  return columns;
};

/**
 * Returns column definitions for the admin groups grid in  global search desktop mode.
 */
const getColumnDefinitionsDesktopSearch = (): ColDef[] => {
  return [{
    headerName   : translate('groups:grid.columns.groupName'),
    field        : GroupsColumns.NAME,
    sortable     : true,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultTextFilterParams
  }, {
    headerName   : translate('groups:grid.columns.itemCount'),
    field        : GroupsColumns.ITEM_COUNT,
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    cellRenderer : 'groupMembersCount',
    filterParams : defaultTextFilterParams
  },
  {
    headerName   : translate('groups:grid.columns.notes'),
    field        : GroupsColumns.NOTES,
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultTextFilterParams
  },
  {
    headerName         : translate('groups:grid.columns.account'),
    field              : GroupsColumns.ACCOUNT,
    cellRenderer       : 'accountName',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : false
  }];
};

const getColumnDefinitions = ({includeGlobalSearch, filteredColumns, showStatus, hasInheritableGroups}) => ({
  desktop : includeGlobalSearch ? getColumnDefinitionsDesktopSearch()
    : getColumnDefinitionsDesktop(filteredColumns, showStatus, hasInheritableGroups),
  mobile : includeGlobalSearch ? getColumnDefinitionsDesktopSearch()
    : getColumnDefinitionsMobile(filteredColumns, showStatus, hasInheritableGroups)
});

export default forwardRef(GroupsGrid);
